import { Link } from "react-router-dom";
import { FileWarning } from "lucide-react";
import { Seo } from "@/components/site/Seo";
import { Breadcrumbs } from "@/components/site/Breadcrumbs";
import { BUSINESS, HEALTH_DISCLAIMER } from "@/config/site";

const addr = `${BUSINESS.address.line1}, ${BUSINESS.address.suburb}, ${BUSINESS.address.city}, ${BUSINESS.address.postalCode}`;

const PAGES = {
  "privacy-policy": {
    title: "Privacy Policy",
    status: "draft",
    description: "Draft privacy policy for Uhlumo Organics describing how personal information is collected and used, in line with POPIA. Pending business approval.",
    sections: [
      ["Who we are", [`Uhlumo Organics (Registration Number ${BUSINESS.registrationNumber}) is the responsible party for personal information collected through this website. Contact: ${BUSINESS.email}, ${BUSINESS.phoneDisplay}, ${addr}.`]],
      [
        "Information we collect",
        [
          "Enquiry forms: first name, surname, email address, phone/WhatsApp number, product of interest, your message, preferred contact method, the page you enquired from and the date of submission.",
          "Community signup: first name, email address, optional phone number and a record of your marketing consent (date, wording and page).",
          "Anonymous usage counts: we count actions such as WhatsApp button clicks without storing personal information or setting cookies.",
          "Analytics cookies (only if enabled and only with your permission).",
        ],
      ],
      [
        "Why we use your information",
        [
          "To respond to your enquiry and provide product information, pricing and ordering details.",
          "To send product news and updates — only if you have given separate, explicit consent. Enquiry consent never signs you up for marketing.",
          "To improve our website and customer service.",
        ],
      ],
      ["Marketing and unsubscribing", ["Every marketing email includes an unsubscribe link. You can also ask us to remove you at any time by emailing " + BUSINESS.email + "."]],
      ["Sharing your information", ["We do not sell your personal information. We may use trusted service providers (for example email delivery and website hosting) who process information on our behalf under appropriate safeguards."]],
      ["Security and retention", ["Customer information is stored securely and access is restricted to authorised staff through a protected admin system. We keep information only for as long as needed for the purposes above or as required by law."]],
      [
        "Your rights",
        [
          "Under the Protection of Personal Information Act (POPIA), you may request access to, correction of, or deletion of your personal information, and object to processing for direct marketing.",
          "You may lodge a complaint with the Information Regulator (South Africa) if you believe your information has been mishandled.",
        ],
      ],
    ],
  },
  terms: {
    title: "Terms & Conditions",
    status: "draft",
    description: "Draft website terms and conditions for Uhlumo Organics. Pending business approval.",
    sections: [
      ["About these terms", ["These terms apply to your use of the Uhlumo Organics website. By using the website you agree to these terms."]],
      ["Product information", ["We aim to keep product information accurate. Ingredients, sizes and other label details are published only once verified. Always read the label on the product you receive.", "Product images may be placeholders or lifestyle images until genuine product photographs are available."]],
      ["Enquiries and orders", ["This website does not process online payments. Submitting an enquiry does not create an order. Prices, availability, payment and delivery are confirmed with you directly before any order is accepted."]],
      ["Health information", HEALTH_DISCLAIMER],
      ["Promotions", ["Any promotion, including community signup offers, applies only when published with its specific terms (validity, eligible products, expiry and redemption)."]],
      ["Consumer rights", ["Nothing in these terms limits your rights under the Consumer Protection Act or other applicable South African law."]],
      ["Contact", [`Questions about these terms: ${BUSINESS.email} or ${BUSINESS.phoneDisplay}.`]],
    ],
  },
  "delivery-returns": {
    title: "Delivery & Returns Information",
    status: "draft",
    description: "Delivery and returns information for Uhlumo Organics. Policy details are being finalised — please contact us to confirm.",
    sections: [
      ["Delivery", ["Delivery and collection options, delivery areas, fees and timeframes are currently confirmed per order. Please contact us with your location before ordering."]],
      ["Returns", ["Our returns policy is being finalised. Please contact us before purchasing if you have questions about returns or exchanges, and keep your proof of purchase.", "Your statutory rights under the Consumer Protection Act are not affected."]],
      ["Questions", [`WhatsApp or call ${BUSINESS.phoneDisplay}, or email ${BUSINESS.email}.`]],
    ],
  },
  "health-disclaimer": {
    title: "Health Disclaimer",
    status: "review",
    description: "Health disclaimer for Uhlumo Organics natural wellness products and website information.",
    sections: [
      ["Health disclaimer", HEALTH_DISCLAIMER],
      ["Product claims", ["We do not claim that our products are approved by SAHPRA or certified organic unless documentary evidence is published. Product classifications and label information are being confirmed.", "Always read the label and follow the manufacturer’s directions and warnings."]],
    ],
  },
};

export default function LegalPage({ page }) {
  const p = PAGES[page];
  const path = `/${page}`;
  return (
    <>
      <Seo title={`${p.title} | Uhlumo Organics`} description={p.description} path={path} noindex={p.status === "draft"} />
      <div className="container-site section">
        <div className="mx-auto max-w-3xl">
          <Breadcrumbs
            items={[
              { name: "Home", path: "/" },
              { name: p.title, path },
            ]}
          />
          <div className="mt-6 flex gap-3 rounded-2xl border p-4 text-sm" style={{ background: "#FFF7E8", borderColor: "rgba(181,71,8,0.25)", color: "#6a3a10" }} role="note" data-testid="legal-draft-banner">
            <FileWarning className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
            <p>
              {p.status === "draft" ? (
                <>
                  <strong>Draft — pending business approval.</strong> This page has been prepared for review by Uhlumo Organics and is not approved legal advice.
                </>
              ) : (
                <>
                  <strong>Subject to final approval.</strong> Wording supplied by the business owner.
                </>
              )}
            </p>
          </div>
          <h1 className="h-display mt-8 text-4xl sm:text-5xl">{p.title}</h1>
          <div className="prose-uhlumo mt-8">
            {p.sections.map(([h, paras]) => (
              <section key={h}>
                <h2>{h}</h2>
                {paras.length > 2 ? (
                  <ul>
                    {paras.map((t) => (
                      <li key={t}>{t}</li>
                    ))}
                  </ul>
                ) : (
                  paras.map((t) => <p key={t}>{t}</p>)
                )}
              </section>
            ))}
          </div>
          <p className="mt-10 text-sm text-muted-foreground">
            Questions? <Link to="/contact" className="link-accent">Contact us</Link>.
          </p>
        </div>
      </div>
    </>
  );
}
